/**
 * Utility functions to parse Markdown frontmatter and serve
 * local dynamic Solutions and Press Releases.
 */

// Simple lightweight Yaml Frontmatter Parser for client-side React (avoids Node.js polyfills)
export function parseFrontmatter(fileContent: string) {
  const match = fileContent.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) {
    return { data: {} as Record<string, any>, content: fileContent };
  }
  const yamlBlock = match[1];
  const content = match[2];
  const data: Record<string, any> = {};
  
  const lines = yamlBlock.split('\n');
  let currentKey = '';
  let inMultiline = false;
  const multilineVal: string[] = [];

  for (let line of lines) {
    line = line.trimEnd();
    
    if (inMultiline) {
      if (line.startsWith('  ') || line === '') {
        multilineVal.push(line.replace(/^  /, ''));
        continue;
      } else {
        data[currentKey] = multilineVal.join('\n');
        inMultiline = false;
        multilineVal.length = 0;
      }
    }

    const colonIndex = line.indexOf(':');
    if (colonIndex !== -1 && !inMultiline) {
      const key = line.slice(0, colonIndex).trim();
      let val = line.slice(colonIndex + 1).trim();
      
      if (val === '|' || val === '>-') {
        currentKey = key;
        inMultiline = true;
        continue;
      }

      if (val.startsWith('"') && val.endsWith('"')) {
        val = val.slice(1, -1);
      } else if (val.startsWith("'") && val.endsWith("'")) {
        val = val.slice(1, -1);
      } else if (val === 'true') {
        val = 'true';
      } else if (val === 'false') {
        val = 'false';
      }
      
      data[key] = val;
    }
  }
  
  if (inMultiline && currentKey) {
    data[currentKey] = multilineVal.join('\n');
  }

  return { data, content };
}

// Custom simple markdown to HTML parser with Tailwind styling support
export function markdownToHtml(markdown: string): string {
  if (!markdown) return '';
  let html = markdown
    // Headings
    .replace(/^### (.*$)/gim, '<h3 class="text-2xl font-bold mt-6 mb-3 text-foreground">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-3xl font-bold mt-8 mb-4 text-foreground">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-4xl font-extrabold mt-10 mb-6 text-foreground">$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-primary hover:underline" target="_blank">$1</a>')
    // Bullets
    .replace(/^\* (.*$)/gim, '<li class="list-disc ml-6 my-1">$1</li>')
    .replace(/^- (.*$)/gim, '<li class="list-disc ml-6 my-1">$1</li>');

  // Wrap or split list items and paragraphs
  const paragraphs = html.split(/\n\s*\n/);
  html = paragraphs.map(p => {
    const trimmed = p.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('<h') || trimmed.startsWith('<li') || trimmed.startsWith('<ul')) return trimmed;
    return `<p class="my-4 leading-relaxed text-muted-foreground">${trimmed}</p>`;
  }).join('\n');

  // Group adjacent <li> tags into <ul> lists
  html = html.replace(/(<li[^>]*>.*?<\/li>\n?)+/g, (match) => `<ul class="my-4 list-disc pl-6 text-muted-foreground">${match}</ul>`);
  
  return html;
}

// Vite Glob loaders for solutions and press releases
const solutionFiles = (import.meta as any).glob('/content/solutions/*.md', { query: '?raw', eager: true });
const pressFiles = (import.meta as any).glob('/content/press-releases/*.md', { query: '?raw', eager: true });

// CMS Solutions Interfaces
export interface CMSSolution {
  slug: string;
  sku: string;
  status: 'published' | 'draft' | 'archived';
  featured: boolean;
  cover_image: string;
  title: string;
  category: string;
  market: string;
  short_description: string;
  seo_title?: string;
  seo_description?: string;
  body: string;
}

// CMS Press Release Interface
export interface CMSPressRelease {
  id: string;
  slug: string;
  status: 'published' | 'draft' | 'archived';
  release_date: string;
  cover_image: string;
  pdf_file?: string;
  title: string;
  excerpt: string;
  seo_title?: string;
  seo_description?: string;
  body: string;
}

// Locale aceito pelo conteúdo local em markdown ('en' cai nos campos _pt)
export type CMSLocale = 'pt' | 'es' | 'en';

// Retrieve Solutions from MD files depending on local context
export function getCMSSolutions(locale: CMSLocale): CMSSolution[] {
  const list: CMSSolution[] = [];

  for (const path in solutionFiles) {
    const fileModule = solutionFiles[path] as { default: string };
    const contentStr = fileModule.default || '';
    const { data } = parseFrontmatter(contentStr);

    if (!data.slug) continue;
    if (data.status !== 'published') continue;

    if (locale === 'es') {
      // Spanish checks
      if (!data.title_es || !data.short_description_es || !data.body_es) {
        // Skip item in Spanish route if translations are incomplete
        continue;
      }
      list.push({
        slug: data.slug,
        sku: data.sku || 'TE-DATASHEET',
        status: data.status,
        featured: data.featured === 'true' || data.featured === true,
        cover_image: data.cover_image || '',
        title: data.title_es,
        category: data.category_es || '',
        market: data.market_es || '',
        short_description: data.short_description_es,
        seo_title: data.seo_title_es,
        seo_description: data.seo_description_es,
        body: markdownToHtml(data.body_es),
      });
    } else {
      // Default / pt
      if (!data.title_pt || !data.short_description_pt || !data.body_pt) {
        continue;
      }
      list.push({
        slug: data.slug,
        sku: data.sku || 'TE-DATASHEET',
        status: data.status,
        featured: data.featured === 'true' || data.featured === true,
        cover_image: data.cover_image || '',
        title: data.title_pt,
        category: data.category_pt || '',
        market: data.market_pt || '',
        short_description: data.short_description_pt,
        seo_title: data.seo_title_pt,
        seo_description: data.seo_description_pt,
        body: markdownToHtml(data.body_pt),
      });
    }
  }

  // Sort: featured first, then by title
  return list.sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return a.title.localeCompare(b.title);
  });
}

// Get solutions map indexed by slug
export function getCMSSolutionBySlug(slug: string, locale: CMSLocale): CMSSolution | null {
  const list = getCMSSolutions(locale);
  return list.find(item => item.slug === slug) || null;
}

// Retrieve Press Releases from MD files depending on local context
export function getCMSPressReleases(locale: CMSLocale): CMSPressRelease[] {
  const list: CMSPressRelease[] = [];

  for (const path in pressFiles) {
    const fileModule = pressFiles[path] as { default: string };
    const contentStr = fileModule.default || '';
    const { data } = parseFrontmatter(contentStr);

    if (!data.slug) continue;
    if (data.status !== 'published') continue;

    if (locale === 'es') {
      if (!data.title_es || !data.excerpt_es || !data.body_es) {
        continue;
      }
      list.push({
        id: data.slug,
        slug: data.slug,
        status: data.status,
        release_date: data.release_date || '',
        cover_image: data.cover_image || '',
        pdf_file: data.pdf_file_es || '',
        title: data.title_es,
        excerpt: data.excerpt_es,
        seo_title: data.seo_title_es,
        seo_description: data.seo_description_es,
        body: markdownToHtml(data.body_es),
      });
    } else {
      // PT-BR / default
      if (!data.title_pt || !data.excerpt_pt || !data.body_pt) {
        continue;
      }
      list.push({
        id: data.slug,
        slug: data.slug,
        status: data.status,
        release_date: data.release_date || '',
        cover_image: data.cover_image || '',
        pdf_file: data.pdf_file_pt || '',
        title: data.title_pt,
        excerpt: data.excerpt_pt,
        seo_title: data.seo_title_pt,
        seo_description: data.seo_description_pt,
        body: markdownToHtml(data.body_pt),
      });
    }
  }

  // Sort: by date reverse order
  return list.sort((a, b) => b.release_date.localeCompare(a.release_date));
}

// Fetch single press release by slug
export function getCMSPressReleaseBySlug(slug: string, locale: CMSLocale): CMSPressRelease | null {
  const list = getCMSPressReleases(locale);
  return list.find(item => item.slug === slug) || null;
}
